import type { AxiosResponse } from 'axios';

import api from '../axios';

export type CalendarScope = 'all' | 'contract';

export interface CalendarSubscriptionUrls {
  ics: string;
  webcal: string;
  google: string;
  outlook: string;
}

export interface CalendarSubscription {
  id: string;
  scope: CalendarScope;
  contractId: string | null;
  displayName: string;
  includePaid: boolean;
  hideAmounts: boolean;
  reminderDaysBefore: number[];
  createdAt: string;
  lastFetchedAt: string | null;
  fetchCount: number;
  urls: CalendarSubscriptionUrls;
}

export interface CreateCalendarSubscriptionBody {
  scope: CalendarScope;
  contractId?: string;
  includePaid?: boolean;
  hideAmounts?: boolean;
  reminderDaysBefore?: number[];
}

export function createCalendarSubscriptionRequest(
  body: CreateCalendarSubscriptionBody,
): Promise<AxiosResponse<CalendarSubscription>> {
  return api.post('/v1/me/calendar-subscriptions', body);
}

export function listCalendarSubscriptionsRequest(
  contractId?: string,
): Promise<AxiosResponse<Omit<CalendarSubscription, 'urls'>[]>> {
  return api.get(
    `/v1/me/calendar-subscriptions${
      contractId ? `?contractId=${encodeURIComponent(contractId)}` : ''
    }`,
  );
}

export function revokeCalendarSubscriptionRequest(
  id: string,
): Promise<AxiosResponse<void>> {
  return api.delete(`/v1/me/calendar-subscriptions/${id}`);
}

export async function downloadCalendarIcs(
  contractId: string | null,
  options: { hideAmounts?: boolean } = {},
): Promise<void> {
  const params = new URLSearchParams();
  if (contractId) params.set('contractId', contractId);
  if (options.hideAmounts) params.set('hideAmounts', 'true');
  const query = params.toString();

  const response = await api.get<Blob>(
    `/v1/me/calendar/export.ics${query ? `?${query}` : ''}`,
    { responseType: 'blob' },
  );

  const blob = new Blob([response.data], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = contractId
    ? 'vencimentos-contrato.ics'
    : 'vencimentos-paga-agro.ics';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
